/**
 * Utilities for detecting text overflow in assignment bars with memoization.
 */
import { measureTextWidth } from '../../../common/utils/measure-text';
import { REM_TO_PX } from '../../constants';
import {
  NOT_BILLABLE_ICON_WIDTH_PX,
  STICKY_LABEL_HORIZONTAL_PADDING_PX,
} from '../../constants/assignment-text';
import { AssignmentType } from '../../types';

interface TextOverflowCheckParams {
  /** The assignment to check */
  assignment: AssignmentType;
  /** Bar width in rem */
  barWidthRem: number;
  /** Text to measure */
  text: string;
  /** Reference element for font measurement */
  textReferenceElement: HTMLElement;
}

/**
 * Memoization cache for text overflow checks.
 * Key: `${assignmentId}_${barWidthRem}_${text}_${isBillable}`
 */
const overflowCache = new Map<string, boolean>();

/**
 * Generates a cache key for overflow check memoization.
 */
const getCacheKey = (
  assignmentId: number,
  barWidthRem: number,
  text: string,
  isBillable: boolean
): string => {
  return `${assignmentId}_${barWidthRem}_${text}_${isBillable}`;
};

/**
 * Clears the overflow check memoization cache.
 * Call this when assignment styles or layout changes significantly.
 */
export const clearOverflowCache = (): void => {
  overflowCache.clear();
};

/**
 * Checks if text fits within the assignment bar without overflowing.
 * Results are memoized for performance.
 *
 * @returns true if text fits, false if it would overflow
 */
export const doesTextFitInBar = ({
  assignment,
  barWidthRem,
  text,
  textReferenceElement,
}: TextOverflowCheckParams): boolean => {
  const assignmentId = assignment.originalAssignment.id;
  const isBillable = assignment.originalAssignment.isBillable;
  const cacheKey = getCacheKey(assignmentId, barWidthRem, text, isBillable);

  // Check cache first
  const cached = overflowCache.get(cacheKey);
  if (cached !== undefined) {
    return cached;
  }

  // Calculate overflow
  const barWidthPx = barWidthRem * REM_TO_PX;
  const iconWidth = isBillable ? 0 : NOT_BILLABLE_ICON_WIDTH_PX;
  const textWidth = measureTextWidth(text, textReferenceElement);
  const totalContentWidth = textWidth + iconWidth;
  const availableWidth = barWidthPx - STICKY_LABEL_HORIZONTAL_PADDING_PX;

  const fits = totalContentWidth <= availableWidth;

  // Store in cache
  overflowCache.set(cacheKey, fits);

  return fits;
};
